import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactApexChart from 'react-apexcharts';
import { legalRegisterApi, processingActivityApi, aiUseCaseApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import type { ProcessingActivity, AiUseCase, DpiaVerdict, AiUseCaseStatus } from '../types/models';
import {
  ACTIVITY_STATUS_LABELS,
  ACTIVITY_STATUS_TONE,
  DPIA_VERDICT_LABELS,
  DPIA_VERDICT_TONE,
} from '../constants/privacy';
import { AI_STATUSES, AI_STATUS_LABELS, AI_STATUS_TONE } from '../constants/aiGovernance';
import { AI_ACT_CLASSIFICATION_LABELS, AI_ACT_CLASSIFICATION_TONE } from '../constants/aiAct';

// Legal Register items come back as loosely-typed rows (see LegalRegisterPage.tsx's own
// `LegalRequirement` type) — same shape reused here rather than a second parallel type.
type LegalRequirement = {
  lawId: string;
  lawName: string;
  status?: string;
};

// Real values only. 'Not Assessed' also covers falsy/missing — matches
// LegalRegisterPage.tsx's own `stats` computation (~line 226) exactly, see legalStats below.
const LEGAL_STATUSES: string[] = ['Compliant', 'Non-Compliant', 'Under Review', 'Not Assessed'];

// Compliant=success-green, Non-Compliant=danger-red, Under Review=info-blue,
// Not Assessed=neutral-gray. A tone name, not a colour — the actual var(--bs-*) token is
// looked up through TONE_CSS below, the same indirection DPIA_VERDICT_TONE / ACTIVITY_STATUS_TONE
// already use elsewhere in this file.
const LEGAL_STATUS_TONE: Record<string, string> = {
  Compliant: 'success',
  'Non-Compliant': 'danger',
  'Under Review': 'info',
  'Not Assessed': 'neutral',
};
const getLegalStatusTone = (status?: string): string => LEGAL_STATUS_TONE[status || 'Not Assessed'] || 'neutral';

// Translates a Nimbus tone name to its underlying chart colour token — the same fixed set of
// var(--bs-*) tokens every dashboard chart in this app draws from, just looked up by tone
// instead of re-stated per caller (mirrors the pct >= 80/50 tone→colour switch in
// SecurityDashboard.tsx's Framework Readiness gauges).
const TONE_CSS: Record<string, string> = {
  success: 'var(--bs-success)',
  danger: 'var(--bs-danger)',
  warning: 'var(--bs-warning)',
  info: 'var(--bs-info)',
  neutral: 'var(--bs-secondary)',
  primary: 'var(--bs-primary)',
  dark: 'var(--bs-dark)',
};

const donutOptions = (
  labels: string[],
  colors: string[],
  center: { value: number; label: string; color: string },
): ApexCharts.ApexOptions => ({
  chart: { type: 'donut' },
  labels,
  colors,
  stroke: { width: 0 },
  dataLabels: { enabled: false },
  legend: { position: 'bottom', fontSize: '13px' },
  plotOptions: {
    pie: {
      donut: {
        size: '68%',
        labels: {
          show: true,
          total: {
            show: true,
            showAlways: true,
            label: center.label,
            color: center.color,
            fontSize: '13px',
            fontWeight: 500,
            formatter: () => String(center.value),
          },
        },
      },
    },
  },
  tooltip: { y: { formatter: (v: number) => `${v} items` } },
});

export const PrivacyDashboard: React.FC = () => {
  const { user } = useAuth();
  const displayName = (user as any)?.name || (user as any)?.email?.split('@')[0] || 'there';

  const { data, isLoading } = useQuery({
    queryKey: ['privacy-dashboard'],
    queryFn: async () => {
      const [legalItems, activities, aiUseCases] = await Promise.all([
        legalRegisterApi.getAll(),
        processingActivityApi.list(),
        aiUseCaseApi.list(),
      ]);
      return {
        legalItems: (legalItems || []) as LegalRequirement[],
        activities: (activities || []) as ProcessingActivity[],
        aiUseCases: (aiUseCases || []) as AiUseCase[],
      };
    },
  });

  const legalItems = data?.legalItems || [];
  const activities = data?.activities || [];
  const aiUseCases = data?.aiUseCases || [];

  // Copied verbatim from LegalRegisterPage.tsx's own `stats` filter logic (~line 226) so the
  // two pages never disagree on what counts as "Not Assessed".
  const legalStats = useMemo(() => ({
    total: legalItems.length,
    compliant: legalItems.filter((i) => i.status === 'Compliant').length,
    nonCompliant: legalItems.filter((i) => i.status === 'Non-Compliant').length,
    underReview: legalItems.filter((i) => i.status === 'Under Review').length,
    notAssessed: legalItems.filter((i) => !i.status || i.status === 'Not Assessed').length,
  }), [legalItems]);

  const dpiaCounts = useMemo(() => {
    const counts: Record<DpiaVerdict, number> = { '': 0, required: 0, not_required: 0, discretionary: 0, incomplete: 0 };
    activities.forEach((a) => { counts[a.dpiaVerdict] = (counts[a.dpiaVerdict] || 0) + 1; });
    return counts;
  }, [activities]);

  const dpiaActionNeeded = dpiaCounts.required + dpiaCounts.incomplete;
  const aiSubmitted = useMemo(() => aiUseCases.filter((u) => u.status === 'submitted').length, [aiUseCases]);
  const aiActive = useMemo(() => aiUseCases.filter((u) => u.status === 'active').length, [aiUseCases]);
  const activeActivities = useMemo(() => activities.filter((a) => a.status === 'active').length, [activities]);
  const legalNeedsAttention = legalStats.nonCompliant + legalStats.underReview;
  const needsAttention = legalNeedsAttention + dpiaActionNeeded;

  const recentLegal = useMemo(() => legalItems.slice(0, 5), [legalItems]);
  const recentActivities = useMemo(() => activities.slice(0, 5), [activities]);
  const recentAiUseCases = useMemo(() => aiUseCases.slice(0, 5), [aiUseCases]);

  const statCards = [
    { label: 'Legal Requirements', value: legalItems.length, sub: `${legalStats.compliant} compliant`, icon: 'ri-scales-3-line' },
    { label: 'Processing Activities', value: activities.length, sub: `${activeActivities} active`, icon: 'ri-database-2-line' },
    { label: 'AI Use Cases', value: aiUseCases.length, sub: `${aiActive} active`, icon: 'ri-robot-line' },
    { label: 'Needs Attention', value: needsAttention, sub: needsAttention > 0 ? 'Needs review' : 'All clear', icon: 'ri-alert-line' },
  ];

  // Legal donut — filtered to statuses actually present, keyed by name (not position) so a
  // filtered-out middle category can never misalign index-to-colour, same reasoning as
  // SecurityDashboard.tsx's donutKeys.
  const legalStatusCount: Record<string, number> = {
    Compliant: legalStats.compliant,
    'Non-Compliant': legalStats.nonCompliant,
    'Under Review': legalStats.underReview,
    'Not Assessed': legalStats.notAssessed,
  };
  const legalDonutKeys = LEGAL_STATUSES.filter((s) => legalStatusCount[s] > 0);
  const legalDonutSeries = legalDonutKeys.map((s) => legalStatusCount[s]);
  const legalDonutColors = legalDonutKeys.map((s) => TONE_CSS[LEGAL_STATUS_TONE[s]] || 'var(--bs-secondary)');
  const legalDonutCenter = {
    value: legalNeedsAttention,
    label: legalNeedsAttention > 0 ? 'Needs attention' : 'All clear',
    color: legalNeedsAttention > 0 ? 'var(--bs-danger-text-emphasis)' : 'var(--bs-success-text-emphasis)',
  };

  // DPIA donut — same "filter to present categories" pattern, labels/colours from the shared
  // DPIA_VERDICT_LABELS / DPIA_VERDICT_TONE constants rather than invented here.
  const dpiaDonutKeys = (Object.keys(DPIA_VERDICT_LABELS) as DpiaVerdict[]).filter((k) => dpiaCounts[k] > 0);
  const dpiaDonutSeries = dpiaDonutKeys.map((k) => dpiaCounts[k]);
  const dpiaDonutLabels = dpiaDonutKeys.map((k) => DPIA_VERDICT_LABELS[k]);
  const dpiaDonutColors = dpiaDonutKeys.map((k) => TONE_CSS[DPIA_VERDICT_TONE[k]] || 'var(--bs-secondary)');
  const dpiaDonutCenter = {
    value: dpiaActionNeeded,
    label: dpiaActionNeeded > 0 ? 'Needs action' : 'All screened',
    color: dpiaActionNeeded > 0 ? 'var(--bs-danger-text-emphasis)' : 'var(--bs-success-text-emphasis)',
  };

  // AI Use Cases donut — same "filter to present categories" pattern as the two above, using
  // the same AI_STATUS_LABELS/AI_STATUS_TONE the AI Use Case Register list page itself uses, so
  // this dashboard never invents its own status vocabulary.
  const aiStatusCount = useMemo(() => {
    const counts: Record<AiUseCaseStatus, number> = { draft: 0, submitted: 0, approved: 0, rejected: 0, active: 0, retired: 0 };
    aiUseCases.forEach((u) => { counts[u.status] = (counts[u.status] || 0) + 1; });
    return counts;
  }, [aiUseCases]);
  const aiDonutKeys = AI_STATUSES.filter((s) => aiStatusCount[s] > 0);
  const aiDonutSeries = aiDonutKeys.map((s) => aiStatusCount[s]);
  const aiDonutLabels = aiDonutKeys.map((s) => AI_STATUS_LABELS[s]);
  const aiDonutColors = aiDonutKeys.map((s) => TONE_CSS[AI_STATUS_TONE[s]] || 'var(--bs-secondary)');
  const aiDonutCenter = {
    value: aiSubmitted,
    label: aiSubmitted > 0 ? 'Awaiting decision' : 'All decided',
    color: aiSubmitted > 0 ? 'var(--bs-warning-text-emphasis)' : 'var(--bs-success-text-emphasis)',
  };

  if (isLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">

      {/* Welcome / Hero Row */}
      <div className="np-grid np-grid-8-4">
        <div className="np-card">
          <div className="np-card-body d-flex align-items-start justify-content-between">
            <div className="flex-grow-1">
              <h2 style={{ marginBottom: 4, fontSize: '1.375rem', fontWeight: 700, color: 'var(--bs-heading-color)', lineHeight: 1.3 }}>Welcome back, {displayName}! 👋</h2>
              <p className="np-sub" style={{ marginBottom: '1rem' }}>Here's what's happening across data protection and AI governance today.</p>
              <div className="row g-3">
                {[
                  { count: legalNeedsAttention, label: 'Legal — Needs Attention', tone: 'danger', icon: 'ri-scales-3-line' },
                  { count: dpiaActionNeeded, label: 'DPIA Action Needed', tone: 'warning', icon: 'ri-file-warning-line' },
                  { count: aiSubmitted, label: 'Awaiting Decision', tone: 'info', icon: 'ri-time-line' },
                  { count: aiActive, label: 'Active AI Use Cases', tone: 'success', icon: 'ri-checkbox-circle-line' },
                ].map((item) => (
                  <div key={item.label} className="col-6 col-md-3">
                    <div className="d-flex align-items-center gap-2">
                      <span className={`np-icon-badge np-tone-${item.tone}`}><i className={item.icon}></i></span>
                      <div>
                        <div className="np-tabular" style={{ fontWeight: 600, lineHeight: 1 }}>{item.count}</div>
                        <small className="np-hint">{item.label}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 d-flex gap-2 flex-wrap">
                <Link to="/legal-register/new" className="np-btn np-btn-primary np-btn-sm"><i className="ri-add-line"></i>New Legal Requirement</Link>
                <Link to="/processing-activities" className="np-btn np-btn-ghost np-btn-sm"><i className="ri-database-2-line"></i>Processing Activities</Link>
                <Link to="/ai-governance" className="np-btn np-btn-ghost np-btn-sm"><i className="ri-robot-2-line"></i>AI Governance</Link>
              </div>
            </div>
            <i className="ri-shield-user-line ms-3 d-none d-md-block" style={{ fontSize: '3rem', color: 'var(--bs-primary)', opacity: 0.25 }}></i>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-head"><h2>Quick Actions</h2></div>
          <div className="np-card-body d-flex flex-column gap-2">
            <Link to="/legal-register/new" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-scales-3-line"></i>New Legal Requirement</Link>
            <Link to="/processing-activities/new" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-database-2-line"></i>New Processing Activity</Link>
            <Link to="/ai-use-cases/new" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-robot-line"></i>New AI Use Case</Link>
            <Link to="/ai-governance" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-robot-2-line"></i>AI Governance Dashboard</Link>
            <Link to="/processing-activities/report" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-file-pdf-2-line"></i>Processing Activity Report</Link>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="np-grid np-grid-4">
        {statCards.map((card) => (
          <div key={card.label} className="np-card">
            <div className="np-card-body np-stat">
              <span className="np-stat-icon np-tone-primary"><i className={card.icon}></i></span>
              <p className="np-stat-label">{card.label}</p>
              <p className="np-stat-value np-tabular">{card.value}</p>
              <p className="np-stat-trend" style={{ color: card.label === 'Needs Attention' && needsAttention > 0 ? 'var(--bs-danger)' : 'var(--bs-success)' }}>
                {card.label === 'Needs Attention' && needsAttention > 0
                  ? <><i className="ri-alert-line"></i>{card.sub}</>
                  : <><i className="ri-arrow-up-line"></i>{card.sub}</>}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="np-grid np-grid-3">
        <div className="np-card">
          <div className="np-card-head"><h2>Legal Register by Status</h2></div>
          <div className="np-card-body">
            {legalItems.length === 0 ? (
              <div className="text-center py-4 np-sub">
                <i className="ri-scales-3-line d-block mb-2" style={{ fontSize: '2rem', opacity: 0.5 }}></i>No legal requirements yet
              </div>
            ) : (
              <ReactApexChart type="donut" height={300} series={legalDonutSeries}
                options={donutOptions(legalDonutKeys, legalDonutColors, legalDonutCenter)} />
            )}
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-head"><h2>Processing Activities by DPIA Verdict</h2></div>
          <div className="np-card-body">
            {activities.length === 0 ? (
              <div className="text-center py-4 np-sub">
                <i className="ri-file-warning-line d-block mb-2" style={{ fontSize: '2rem', opacity: 0.5 }}></i>No processing activities yet
              </div>
            ) : (
              <ReactApexChart type="donut" height={300} series={dpiaDonutSeries}
                options={donutOptions(dpiaDonutLabels, dpiaDonutColors, dpiaDonutCenter)} />
            )}
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-head"><h2>AI Use Cases by Status</h2></div>
          <div className="np-card-body">
            {aiUseCases.length === 0 ? (
              <div className="text-center py-4 np-sub">
                <i className="ri-robot-line d-block mb-2" style={{ fontSize: '2rem', opacity: 0.5 }}></i>No AI use cases yet
              </div>
            ) : (
              <ReactApexChart type="donut" height={300} series={aiDonutSeries}
                options={donutOptions(aiDonutLabels, aiDonutColors, aiDonutCenter)} />
            )}
          </div>
        </div>
      </div>

      {/* Recent legal requirements + processing activities + AI use cases */}
      <div className="np-grid np-grid-3">
        <div className="np-card">
          <div className="np-card-head">
            <h2>Recent Legal Requirements</h2>
            <Link to="/legal-register" className="np-btn np-btn-ghost np-btn-sm">View All</Link>
          </div>
          <div className="np-card-body" style={{ padding: 0 }}>
            <div className="np-table-wrap">
              <table className="np-table">
                <thead><tr><th>Title</th><th>Status</th></tr></thead>
                <tbody>
                  {recentLegal.length === 0 ? (
                    <tr><td colSpan={2} className="text-center np-sub py-4"><i className="ri-scales-3-line me-1"></i>No legal requirements found</td></tr>
                  ) : recentLegal.map((item) => (
                    <tr key={item.lawId}>
                      <td><Link to={`/legal-register/${item.lawId}/edit`} className="np-row-title">{item.lawName}</Link></td>
                      <td>
                        <span className={`np-chip np-tone-${getLegalStatusTone(item.status)}`}>
                          {item.status || 'Not Assessed'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-head">
            <h2>Recent Processing Activities</h2>
            <Link to="/processing-activities" className="np-btn np-btn-ghost np-btn-sm">View All</Link>
          </div>
          <div className="np-card-body" style={{ padding: 0 }}>
            <div className="np-table-wrap">
              <table className="np-table">
                <thead><tr><th>Title</th><th>Status</th><th>DPIA Verdict</th></tr></thead>
                <tbody>
                  {recentActivities.length === 0 ? (
                    <tr><td colSpan={3} className="text-center np-sub py-4"><i className="ri-database-2-line me-1"></i>No processing activities found</td></tr>
                  ) : recentActivities.map((item) => (
                    <tr key={item.activityId}>
                      <td><Link to={`/processing-activities/${item.activityId}`} className="np-row-title">{item.name}</Link></td>
                      <td><span className={`np-chip np-tone-${ACTIVITY_STATUS_TONE[item.status]}`}>{ACTIVITY_STATUS_LABELS[item.status]}</span></td>
                      <td><span className={`np-chip np-tone-${DPIA_VERDICT_TONE[item.dpiaVerdict]}`}>{DPIA_VERDICT_LABELS[item.dpiaVerdict]}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-head">
            <h2>Recent AI Use Cases</h2>
            <Link to="/ai-use-cases" className="np-btn np-btn-ghost np-btn-sm">View All</Link>
          </div>
          <div className="np-card-body" style={{ padding: 0 }}>
            <div className="np-table-wrap">
              <table className="np-table">
                <thead><tr><th>Title</th><th>Status</th><th>AI Act Tier</th></tr></thead>
                <tbody>
                  {recentAiUseCases.length === 0 ? (
                    <tr><td colSpan={3} className="text-center np-sub py-4"><i className="ri-robot-line me-1"></i>No AI use cases found</td></tr>
                  ) : recentAiUseCases.map((item) => (
                    <tr key={item.useCaseId}>
                      <td><Link to={`/ai-use-cases/${item.useCaseId}`} className="np-row-title">{item.name}</Link></td>
                      <td><span className={`np-chip np-tone-${AI_STATUS_TONE[item.status]}`}>{AI_STATUS_LABELS[item.status]}</span></td>
                      <td><span className={`np-chip np-tone-${AI_ACT_CLASSIFICATION_TONE[item.aiActClassification]}`}>{AI_ACT_CLASSIFICATION_LABELS[item.aiActClassification]}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};
